import { useEffect, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'

import { ApolloError } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'

import { getCopy } from '~/utils/clubConfig/utils/getCopy'
import { formatConsentToAPIFormat } from '~/utils/formatConsentToAPIFormat'

import { useCreateUserMutation, User, UserDataDocument, useUserDataQuery } from '~/app/generated/graphql'
import { PhoneInput } from '~/components/PhoneInput'
import { useMarketContext } from '~/components/SettingsProvider/MarketClientContext'
import { gt } from '~/lib/gt'

import Checkbox from '../../Checkbox'
import { FormInput } from '../../FormInput/FormInput'
import Icon from '../../Icon/IconView'
import { useToast } from '../../Toast'
import sharedStyles from '../AuthModals.module.css'
import { ModalTitle, SocialButtons, SubmitButton } from '../AuthModalsShards'
import { formatErrorMessages } from '../LoginModal/formatErrorMessages'

import styles from './RegisterModal.module.css'
import { getConsents, registerNowText, registerSchema } from './RegisterModalUtils'

const mergedStyles = { ...styles, ...sharedStyles }

export type RegisterSignInCallback = (user: Pick<User, 'id'> | null, social: boolean) => void

type RegisterModalProps = {
  handleClose: () => void
  onLoginLinkClick?: () => void
  onSuccess?: RegisterSignInCallback
}

const IconCheck = () => <Icon icon='check-bold' className={mergedStyles.iconCheck} />

const defaultValues = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  password: '',
  marketingConsentAccepted: false,
  regulationConsentAccepted: false,
}

export const RegisterModal = ({ handleClose, onLoginLinkClick, onSuccess }: RegisterModalProps) => {
  return (
    <div className={mergedStyles.modal} data-color-scheme='white'>
      <div className={mergedStyles.formWrapper}>
        <RegisterForm handleClose={handleClose} onLoginLinkClick={onLoginLinkClick} onSuccess={onSuccess} />
      </div>
      <div className={mergedStyles.imageWrapper}>
        <div className={mergedStyles.textWrapper}>
          <h4 className={mergedStyles.benefitsTitle}>{gt.tp('RegisterForm', 'Register your Guest Account today!')}</h4>
          <h6 className={mergedStyles.benefitsSubitle}>{gt.tp('RegisterForm', "What you'll gain:")}</h6>
          {registerNowText.map(text => (
            <h6 className={mergedStyles.benefitsBullet} key={text}>
              <IconCheck /> {gt.tp('RegisterForm', text)}
            </h6>
          ))}
        </div>
      </div>
    </div>
  )
}

export const RegisterForm = ({ handleClose, onLoginLinkClick, onSuccess }: RegisterModalProps) => {
  const { refetch } = useUserDataQuery()
  const [createUser] = useCreateUserMutation()
  const toast = useToast()
  const { supportEmail, copyKey, defaultPhoneNumberCountry } = useMarketContext()

  const consents = getConsents(gt, { ...getCopy(copyKey, gt).marketingConsent })
  const consentNames = consents.map(c => c.name)

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(registerSchema),
  })

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting, isValid, touchedFields, isSubmitted },
  } = formMethods

  const handleErrors = (error: ApolloError) => {
    const erros = formatErrorMessages(error, { supportEmail })

    erros?.map(error =>
      toast?.add({
        message: error,
        type: 'error',
        containerSelector: '#RegisterToastContainer',
        size: 'small',
      })
    )
  }

  const onSocialSignUp: RegisterSignInCallback = async user => {
    await refetch()
    if (user) onSuccess?.(user, true)
    handleClose && handleClose()
  }

  const onSubmit = async (values: any) => {
    await createUser({
      variables: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        phoneNumber: values.phoneNumber,
        consents: consents.map(consent => formatConsentToAPIFormat({ ...consent, value: values[consent.name] })),
      },
      onCompleted: data => {
        onSuccess && onSuccess(data?.createUser.user ?? null, false)
      },
      refetchQueries: [{ query: UserDataDocument }, 'UserData'],
      onError: error => handleErrors(error),
    })
  }

  const getFieldErrorMessage = (name: keyof typeof defaultValues) => (touchedFields[name] ? errors[name]?.message : '')

  const consentValues = useWatch({ control, name: consentNames }) as boolean[] | undefined

  const allSelected = !!consentValues?.length && consentValues.every(Boolean)
  const someSelected = !!consentValues?.some(Boolean)

  const [selectAll, setSelectAll] = useState(false)

  useEffect(() => {
    setSelectAll(allSelected)
  }, [allSelected])

  const handleSelectAllChange = (checked: boolean) => {
    setSelectAll(checked)
    consentNames.forEach(name => setValue(name, checked, { shouldDirty: true, shouldValidate: true }))
  }

  return (
    <FormProvider {...formMethods}>
      <form className={mergedStyles.form} onSubmit={handleSubmit(onSubmit)}>
        <ModalTitle title={gt.tp('RegisterForm', 'Register')} />

        <h5 className={mergedStyles.noAccount}>
          {gt.tp('RegisterForm', 'Already have an account?')}
          <button type='button' className={mergedStyles.noAccountButton} onClick={onLoginLinkClick}>
            {gt.tp('RegisterForm', 'Log in here')}
          </button>
        </h5>

        <div id='RegisterToastContainer' className={mergedStyles.toastContainer} />
        <div className={mergedStyles.styledInputs}>
          <FormInput
            {...register('firstName')}
            type={'text'}
            label={gt.tp('LoginForm', 'First name*')}
            className={mergedStyles.input}
            errorMessage={getFieldErrorMessage('firstName')}
          />

          <FormInput
            {...register('lastName')}
            type={'text'}
            label={gt.tp('LoginForm', 'Last name*')}
            className={mergedStyles.input}
            errorMessage={getFieldErrorMessage('lastName')}
          />

          <FormInput
            {...register('email')}
            type={'email'}
            label={gt.tp('LoginForm', 'Email*')}
            className={mergedStyles.input}
            errorMessage={getFieldErrorMessage('email')}
          />

          <PhoneInput
            initialCountryCode={defaultPhoneNumberCountry}
            title={`${gt.tp('LoginForm', 'Phone')}*`}
            name='phoneNumber'
            error={getFieldErrorMessage('phoneNumber')}
          />

          <FormInput
            {...register('password')}
            type={'password'}
            label={gt.tp('LoginForm', 'Password*')}
            className={mergedStyles.input}
            errorMessage={getFieldErrorMessage('password')}
          />
        </div>
        <div className={mergedStyles.checkboxWrapperUpper}>
          <Checkbox
            key={`select-all-${selectAll ? 'all' : someSelected ? 'some' : 'none'}`}
            checked={selectAll}
            onChange={handleSelectAllChange}
            label={gt.tp('RegisterForm', 'Select all consents')}
            disabled={isSubmitting}
            className={mergedStyles.linkCheckbox}
          />
        </div>

        <div className={mergedStyles.checkboxWrapper}>
          {consents.map(consent => (
            <Checkbox
              key={consent.name}
              name={consent.name}
              control={control}
              label={consent.label}
              showMore={consent.showMore}
              error={isSubmitted && !!errors[consent.name]}
              disabled={isSubmitting}
              className={mergedStyles.linkCheckbox}
            />
          ))}
        </div>
        <p className={mergedStyles.consentNotice}>
          {gt.tp(
            'ConsentNotice',
            'If you do not agree to the communication, you will lose the ability to contact you about your bookings. It will also prevent us from communicating booking benefits to you.'
          )}
        </p>

        <SubmitButton disabled={!isValid || isSubmitting} label={gt.tp('RegisterForm', 'Register')} />
        <div className={mergedStyles.otherLogin}>{gt.tp('Or', 'or')}</div>

        <SocialButtons
          onLogin={onSocialSignUp}
          appleButtonProps={{ label: gt.tp('Register', 'Sign up with Apple') }}
          googleButtonProps={{ label: gt.tp('Register', 'Sign up with Google') }}
        />
      </form>
    </FormProvider>
  )
}
